import fetcher from "src/utils/fetcher"
import getConfig from "next/config"
import cookie from "js-cookie"
import { NPS_CLOSE_RECHECK_MINUTES, NPS_CLOSE_RECHECK_MINUTES_LONG, NPS_ELIGIBLE_RECHECK_MINUTES, NPS_MIN_FAVORITES, NPS_MIN_FICHA_VIEWS, NPS_MIN_LEADS, NPS_MIN_SEARCHES, NPS_MIN_SHARES } from "./npsSurvey.constants"

const { API_URL } = getConfig().publicRuntimeConfig

const getNpsApiBase = (): string => API_URL ?? ""

const NPS_UUID_STORAGE_KEY = "nps_uuid"

// site_name llega tal cual lo define publicRuntimeConfig (con tildes/mayúsculas)
// site_id permitidos por backend: infocasas_uy, infocasas_bo, infocasas_py, infocasas_pe, infocasas_br, fincaraiz, cee
const DIACRITICS_REGEX = new RegExp("[\\u0300-\\u036f]", "g")
const normalizeSiteName = (value: string): string =>
    value.normalize("NFD").replace(DIACRITICS_REGEX, "").toLowerCase()

export const getNpsSiteId = (siteName: string, countryCode: string): string | undefined => {
    if (!siteName || !countryCode) return undefined
    if(countryCode === 'CO') return normalizeSiteName(siteName);
    return `${normalizeSiteName(siteName)}_${countryCode.toLowerCase()}`;
}

const generateUuid = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === "x" ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

export const getNpsUuid = (): string => {
    if (typeof window === "undefined") return ""
    let uuid = localStorage.getItem(NPS_UUID_STORAGE_KEY)
    if (!uuid) {
        uuid = generateUuid()
        localStorage.setItem(NPS_UUID_STORAGE_KEY, uuid)
    }
    return uuid
}

const getAuthHeaders = (): Record<string, string> => {
    const token = cookie.get("frontend_token")
    const origin = typeof window !== "undefined" ? window.location.host : undefined
    return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(origin ? { "x-origin": origin } : {}),
    }
}

// El backend responde eligible=false durante los primeros NPS_ELIGIBLE_RECHECK_MINUTES
// minutos desde que se creó el uuid (siempre, sin importar interacciones). Por eso este
// cache expira solo: al vencer, checkNpsEligibility vuelve a golpear /eligible en vez de
// confiar indefinidamente en el false cacheado.
const getNpsNotEligibleCookieKey = (siteId: string): string => `nps_not_eligible_${siteId}`
// La página de búsqueda remonta en cada navegación (Next.js recrea el árbol en cada
// cambio de query), así que NpsSurveyCard vuelve a montarse y a llamar /eligible en
// cada búsqueda. Cacheamos también el resultado eligible=true por sesión para no
// re-consultar el backend en cada remount dentro de la misma sesión de navegador.
const getNpsIsEligibleCookieKey = (siteId: string): string => `nps_is_eligible_${siteId}`
// Bloqueo corto tras el 1er/2do cierre sin responder: NO es una marca de "no eligible"
// (no toca nps_not_eligible_* ni nps_is_eligible_*), solo evita que el popup reaparezca
// de inmediato en un remount cercano. Recién al 3er cierre se marca "no eligible" de
// verdad (ver sendNpsResponse).
const getNpsCloseSnoozeCookieKey = (siteId: string): string => `nps_close_snooze_${siteId}`

// --- Tracking de interacciones (búsquedas, fichas vistas, leads enviados, favoritos, shares) ---
// Cookie persistente: se conserva entre sesiones y solo se limpia cuando el usuario
// completa (no salta) el formulario NPS con éxito contra /response.
// Suma siempre, sin importar el estado de elegibilidad conocido: el backend puede tardar
// (p. ej. el uuid recién creado no es elegible hasta pasados NPS_ELIGIBLE_RECHECK_MINUTES
// minutos) y no queremos perder el progreso de interacción del usuario mientras tanto.
const NPS_INTERACTIONS_COOKIE_KEY = "nps_interactions"
const NPS_INTERACTIONS_EXPIRES_DAYS = 3650

type NpsInteractionCounts = {
    searches: number;
    fichaViews: number;
    leadsSent: number;
    favoritesAdded: number;
    shares: number;
}

const DEFAULT_NPS_INTERACTIONS: NpsInteractionCounts = {
    searches: 0,
    fichaViews: 0,
    leadsSent: 0,
    favoritesAdded: 0,
    shares: 0,
}

const getNpsInteractions = (): NpsInteractionCounts => {
    if (typeof document === "undefined") return DEFAULT_NPS_INTERACTIONS
    try {
        const raw = cookie.get(NPS_INTERACTIONS_COOKIE_KEY)
        return raw ? { ...DEFAULT_NPS_INTERACTIONS, ...JSON.parse(raw) } : DEFAULT_NPS_INTERACTIONS
    } catch {
        return DEFAULT_NPS_INTERACTIONS
    }
}

const setNpsInteractions = (interactions: NpsInteractionCounts): void => {
    if (typeof document === "undefined") return
    cookie.set(NPS_INTERACTIONS_COOKIE_KEY, JSON.stringify(interactions), {
        expires: NPS_INTERACTIONS_EXPIRES_DAYS,
    })
}

// El componente NpsSurveyCard solo evalúa elegibilidad al montarse, y páginas como
// SearchPage no remontan entre búsquedas (solo re-renderizan). Sin este evento, el
// card no se entera de que se alcanzó el umbral hasta un refresh completo.
export const NPS_INTERACTION_EVENT = "nps-interaction-updated"

const bumpNpsInteraction = (field: keyof NpsInteractionCounts): void => {
    const interactions = getNpsInteractions()
    setNpsInteractions({ ...interactions, [field]: interactions[field] + 1 })
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(NPS_INTERACTION_EVENT))
    }
}

export const trackNpsSearch = (): void => bumpNpsInteraction("searches")
export const trackNpsFichaView = (): void => bumpNpsInteraction("fichaViews")
export const trackNpsLeadSent = (): void => bumpNpsInteraction("leadsSent")
export const trackNpsFavoriteAdded = (): void => bumpNpsInteraction("favoritesAdded")
// Se cuenta cada vez que comparte por fb/twitter/whatsapp o copia el link (no imprimir)
export const trackNpsShare = (): void => bumpNpsInteraction("shares")

const clearNpsInteractions = (): void => {
    if (typeof document === "undefined") return
    cookie.remove(NPS_INTERACTIONS_COOKIE_KEY)
}

// Cuenta cuántas veces el usuario cerró el formulario sin responder (onClose), para ir
// aumentando el tiempo de bloqueo del popup en cierres sucesivos. Persistente como
// nps_interactions: se resetea solo cuando el usuario completa el formulario con éxito.
const NPS_CLOSE_COUNT_COOKIE_KEY = "nps_close_count"

const getNpsCloseCount = (): number => {
    if (typeof document === "undefined") return 0
    const raw = cookie.get(NPS_CLOSE_COUNT_COOKIE_KEY)
    const count = raw ? parseInt(raw, 10) : 0
    return Number.isNaN(count) ? 0 : count
}

const bumpNpsCloseCount = (): number => {
    if (typeof document === "undefined") return 0
    const count = getNpsCloseCount() + 1
    cookie.set(NPS_CLOSE_COUNT_COOKIE_KEY, String(count), {
        expires: NPS_INTERACTIONS_EXPIRES_DAYS,
    })
    return count
}

const clearNpsCloseCount = (): void => {
    if (typeof document === "undefined") return
    cookie.remove(NPS_CLOSE_COUNT_COOKIE_KEY)
}

const setNpsCloseSnooze = (siteId: string, minutes: number): void => {
    if (typeof document === "undefined") return
    cookie.set(getNpsCloseSnoozeCookieKey(siteId), "1", {
        expires: new Date(Date.now() + minutes * 60 * 1000),
    })
}

const clearNpsCloseSnooze = (siteId: string): void => {
    if (typeof document === "undefined") return
    cookie.remove(getNpsCloseSnoozeCookieKey(siteId))
}

// Al loguearse o desloguearse queremos una consulta fresca a /eligible en vez de confiar
// en la cache de elegibilidad de la sesión anterior (el uuid NO se toca, sigue estable
// entre login/logout). Solo se borra el cache de is_eligible/not_eligible y se dispara el
// evento para que un NpsSurveyCard montado vuelva a llamar a checkNpsEligibility.
export const revalidateNpsEligibility = (siteId: string): void => {
    if (typeof document !== "undefined") {
        cookie.remove(getNpsIsEligibleCookieKey(siteId))
        cookie.remove(getNpsNotEligibleCookieKey(siteId))
    }
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(NPS_INTERACTION_EVENT))
    }
}

const meetsNpsInteractionThreshold = (): boolean => {
    const { searches, fichaViews, leadsSent, favoritesAdded, shares } = getNpsInteractions()
    return (
        searches >= NPS_MIN_SEARCHES ||
        fichaViews >= NPS_MIN_FICHA_VIEWS ||
        leadsSent >= NPS_MIN_LEADS ||
        favoritesAdded >= NPS_MIN_FAVORITES ||
        shares >= NPS_MIN_SHARES
    )
}

// checkNpsEligibility puede dispararse casi al mismo tiempo desde dos lugares (el mount
// de NpsSurveyCard y el evento nps-interaction-updated que trackNpsSearch dispara en la
// carga inicial de la página). Mientras la primera llamada está en vuelo todavía no hay
// cookie guardada, así que sin este cache la segunda llamada también golpearía /eligible.
const eligibilityRequestsInFlight = new Map<string, Promise<boolean>>()

export const checkNpsEligibility = async (siteId: string): Promise<boolean> => {
    if (cookie.get(getNpsCloseSnoozeCookieKey(siteId)) === "1") {
        return false
    }

    const notEligibleCookieKey = getNpsNotEligibleCookieKey(siteId)
    if (cookie.get(notEligibleCookieKey) === "1") {
        return false
    }

    const isEligibleCookieKey = getNpsIsEligibleCookieKey(siteId)
    if (cookie.get(isEligibleCookieKey) === "1") {
        // Ya sabemos que esta sesión es eligible=true: no volvemos a golpear /eligible en
        // cada remount de la página de búsqueda, solo reevaluamos el umbral localmente.
        return meetsNpsInteractionThreshold()
    }

    const inFlight = eligibilityRequestsInFlight.get(siteId)
    if (inFlight) return inFlight

    const request = checkNpsEligibilityRequest(siteId, notEligibleCookieKey, isEligibleCookieKey).finally(() => {
        eligibilityRequestsInFlight.delete(siteId)
    })
    eligibilityRequestsInFlight.set(siteId, request)
    return request
}

const checkNpsEligibilityRequest = async (
    siteId: string,
    notEligibleCookieKey: string,
    isEligibleCookieKey: string
): Promise<boolean> => {
    try {
        const params = new URLSearchParams({
            uuid: getNpsUuid(),
            site_id: siteId,
        })
        const response = await fetcher<{ eligible: boolean }>(`${getNpsApiBase()}/api/nps/eligible?${params.toString()}`, {
            method: "GET",
            headers: getAuthHeaders(),
        })

        if (response.eligible === false) {
            // Refleja el estado real del backend. Se cachea solo por NPS_ELIGIBLE_RECHECK_MINUTES
            // minutos: pasado ese tiempo (p. ej. porque el uuid recién cumplió la antigüedad
            // mínima que exige el backend) volvemos a consultar /eligible en vez de asumir
            // que sigue en false indefinidamente. Los contadores de interacción NO se tocan:
            // deben seguir sumando aunque el backend todavía diga que no es elegible.
            cookie.set(notEligibleCookieKey, "1", {
                expires: new Date(Date.now() + NPS_ELIGIBLE_RECHECK_MINUTES * 60 * 1000),
            })
            return false
        }

        if (response.eligible === true) {
            // Cookie de sesión: mientras dure esta sesión de navegador no volvemos a golpear
            // /eligible (la página de búsqueda remonta en cada navegación).
            cookie.set(isEligibleCookieKey, "1")
            return meetsNpsInteractionThreshold()
        }

        // Respuesta ambigua (fetcher no lanza si el status es 200/201 pero el body no
        // parsea como JSON válido; en ese caso devuelve {} y `eligible` queda undefined).
        // No es una respuesta real del backend: no tocamos ninguna cookie, se reintenta
        // la próxima vez sin asumir nada.
        return false
    } catch {
        return false
    }
}

// Tras responder o cerrar el formulario (con o sin enviar puntaje) no queremos que el
// popup pueda volver a aparecer en un remount inmediato de la misma sesión (p. ej. la
// página de búsqueda remonta en cada navegación). Lo marcamos "no eligible" con el mismo
// mecanismo que usa el backend, así se re-consulta /eligible recién pasados los minutos
// indicados.
const markNpsNotEligible = (siteId: string, minutes: number): void => {
    if (typeof document === "undefined") return
    cookie.set(getNpsNotEligibleCookieKey(siteId), "1", {
        expires: new Date(Date.now() + minutes * 60 * 1000),
    })
    cookie.remove(getNpsIsEligibleCookieKey(siteId))
}

export const sendNpsResponse = async (
    siteId: string,
    score: number | undefined,
    comment: string,
    skipped: boolean = false
): Promise<boolean> => {
    try {
        await fetcher(`${getNpsApiBase()}/api/nps/response`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
                uuid: getNpsUuid(),
                site_id: siteId,
                skipped,
                ...(skipped ? {} : { score }),
                comment,
            }),
        })
        if (skipped) {
            const closeCount = bumpNpsCloseCount()
            if (closeCount >= 3) {
                // Recién al 3er cierre sin responder se marca "no eligible" de verdad.
                markNpsNotEligible(siteId, NPS_CLOSE_RECHECK_MINUTES_LONG)
            } else {
                // 1ra y 2da vez: solo un bloqueo corto, sin tocar la eligibilidad.
                setNpsCloseSnooze(siteId, NPS_CLOSE_RECHECK_MINUTES)
            }
        } else {
            markNpsNotEligible(siteId, NPS_ELIGIBLE_RECHECK_MINUTES)
            clearNpsInteractions()
            clearNpsCloseCount()
            clearNpsCloseSnooze(siteId)
        }
        return true
    } catch {
        return false
    }
}


